import { Fragment, MouseEvent, useCallback, useState } from "react";
import { Button, IconButton, Popover } from "@mui/material";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { useChatSurfaceContext } from "../../context/chat-surface-context";
import type { ChatSurfaceSourceType, SummaryProps } from "../../context/types";
import { DotsTyping } from "../dots-typing";
import { AgenticActivityFeed } from "../activity-feed";
import type { ActivityFeed } from "../../agentic/activity-feed";
import "./styles.css";

/* eslint-disable @typescript-eslint/no-explicit-any */

export enum ChatBubbleType {
  question,
  answer,
  summary,
  typing,
  error,
  tivaData,
}

export enum Feedback {
  like = "1",
  dislike = "-1",
  noReview = "0",
}

/** Per-resource visibility mode (mirrors backend CitationDisplay). */
export type CitationDisplayMode =
  | "HIDDEN"
  | "FILE_ONLY"
  | "PAGES_INFO"
  | "PAGES_LINKED";

export interface MediaSource {
  source_type: ChatSurfaceSourceType;
  source?: string;
  citationDisplay?: CitationDisplayMode;
  pageList?: { number: number; lastNumber?: number; quote?: string }[];
  timeList?: {
    startSeconds: number;
    endSeconds?: number;
    startLabel?: string;
    endLabel?: string;
    chapterTitle?: string;
    quote?: string;
  }[];
  [extra: string]: any;
}

export interface MediaButtonProps {
  source: MediaSource;
  showQuotes?: boolean;
  handleLink: (arg: any) => void;
  className?: string;
}

/** One agentic citation entry (v2.1.0 P2-09, design §9). */
export interface AgenticCitation {
  marker: string;
  title: string;
  name?: string;
  role?: "primary" | "supporting";
  locator?: { kind: string; value: number | string | null } | null;
  quote?: string | null;
  quoteVerified?: boolean;
  [extra: string]: any;
}

export interface ChatBubbleProps {
  id?: number;
  message: string;
  isLoading: boolean;
  type: ChatBubbleType;
  /**
   * BCP-47 language tag for the message text (e.g. "es", "fr"). Applied as the
   * `lang` attribute on the answer text so a screen reader switches voice for a
   * non-English answer (WCAG 3.1.2). Omit / undefined = inherit the page language.
   */
  lang?: string;
  data?: any;
  metadata?: any[];
  handleLink?: (arg: any) => void;
  /**
   * Spreadsheet citations (sheet-citations design §4.3): open the host's
   * in-app sheet excerpt viewer for a `sheet`-locator citation (the
   * citation carries `messageId` like handleLink's argument). When absent,
   * sheet locators fall back to handleLink (whole-file open).
   */
  handleSheetView?: (citation: AgenticCitation) => void;
  handleTivaDataResult?: (arg: any) => void;
  handleSummarize?: (summaryData: SummaryProps) => void;
  handleReview?: (id: number, feedback: Feedback) => void;
  isActive?: boolean;
  review?: Feedback;
  showReview?: boolean;
  showSources?: boolean;
  showSourcesQuotes?: boolean;
  showSummary?: boolean;
  hideAvatars?: boolean;
  isExpanded?: boolean;
  setExpanded?: (expanded: boolean) => void;
  /**
   * Agentic path (P3-06): grouped activity feed built by
   * `applyActivityEvent` from the typed stream events. Expanded + announced
   * while streaming, collapsed behind a disclosure once the turn is done.
   * Replaces the P2-09 `activity: string[]` prop.
   */
  activityFeed?: ActivityFeed;
  /** Agentic path: budget-exhausted best-effort answer flag (D7). */
  bestEffort?: boolean;
  /**
   * Agentic path: operational tool failures recorded on the turn
   * (payload.flags.toolErrors — e.g. a search backend error). Rendered as a
   * persistent error banner so infrastructure problems are visible in chat
   * instead of only in the flow logs.
   */
  toolErrors?: string[];
}

function fmtTime(secs: number): string {
  if (!Number.isFinite(secs)) return "";
  const total = Math.floor(secs);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

function timeLabel(entry: any): string {
  const startTxt = entry.startLabel || fmtTime(entry.startSeconds);
  const endTxt = entry.endLabel || fmtTime(entry.endSeconds);
  if (entry.chapterTitle) return `${entry.chapterTitle} (${startTxt})`;
  return endTxt && endTxt !== startTxt ? `${startTxt} - ${endTxt}` : startTxt;
}

const isVideo = (type: ChatSurfaceSourceType) =>
  type === "video" || type === "mp4";
const isAudio = (type: ChatSurfaceSourceType) =>
  type === "mp3" || type === "m4a";
const isMedia = (type: ChatSurfaceSourceType) => isVideo(type) || isAudio(type);
const hasPages = (source: MediaSource) =>
  source.source_type === "pdf" &&
  !!source.pageList?.filter((p) => p.number > 0).length;
const hasTimes = (source: MediaSource) =>
  isMedia(source.source_type) && !!source.timeList?.length;

export function MediaButton({
  source,
  showQuotes = true,
  handleLink,
  className = "",
}: MediaButtonProps) {
  const { icons } = useChatSurfaceContext();
  const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
  const menuOpen = Boolean(menuAnchor);

  const StartIcon = isMedia(source.source_type)
    ? icons.Play
    : source.source_type === "pdf"
      ? icons.Pdf
      : icons.Txt;
  const EndIcon = menuOpen ? icons.ArrowCollapse : icons.ArrowExpand;

  // Resource visibility mode (default PAGES_LINKED preserves legacy behavior).
  const mode: CitationDisplayMode = source.citationDisplay ?? "PAGES_LINKED";
  const isFileOnly = mode === "FILE_ONLY";
  const isInformational = mode === "PAGES_INFO";
  // Expandable (shows the page/time menu) unless the resource is filename-only.
  const expandable = (hasPages(source) || hasTimes(source)) && !isFileOnly;

  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    if (isFileOnly) return; // informational filename only — no open, no menu
    if (expandable) {
      setMenuAnchor(event.currentTarget);
      return;
    }
    if (isInformational) return; // informational — never opens the file
    handleLink(source); // PAGES_LINKED with no pages → open the whole file
  };

  const sourceName = (item: MediaSource) => {
    if (item.source) return item.source;
    if (isVideo(item.source_type)) return "Video link";
    if (isAudio(item.source_type)) return "Audio link";
    if (item.source_type === "pdf") return "PDF link";
    return "Document link";
  };

  return (
    <Fragment>
      <Button
        className={`cs-media-button ${className}`}
        variant="text"
        startIcon={<StartIcon className="cs-media-button__icon--start" />}
        endIcon={
          expandable ? <EndIcon className="cs-media-button__icon--end" /> : null
        }
        onClick={handleMenuClick}
      >
        <span className="cs-media-button__label">{sourceName(source)}</span>
      </Button>
      <Popover
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={() => setMenuAnchor(null)}
        slotProps={{
          paper: { elevation: 0, className: "cs-media-button-menu" },
        }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      >
        <div
          className={`cs-media-button-menu-scroll ${
            !showQuotes ? "cs-media-button-menu-scroll--no-quotes" : ""
          }`}
        >
          {!showQuotes && (
            <div className="cs-media-button-menu-title">
              {hasTimes(source)
                ? `The most relevant moments from this ${
                    isAudio(source.source_type) ? "recording" : "video"
                  }:`
                : "The most relevant pages from this file:"}
            </div>
          )}
          {hasPages(source) &&
            source.pageList!.map((page, index) => (
              <Fragment key={`page-${index}-${page.number}`}>
                {showQuotes ? (
                  <div className="cs-media-button-menu-item">
                    {index > 0 && (
                      <div className="cs-media-button-menu-item-separator" />
                    )}
                    {isInformational ? (
                      <span className="cs-media-button-menu-item-title cs-media-button-menu-item-title--static">
                        page {page.number}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="cs-media-button-menu-item-title"
                        onClick={() =>
                          handleLink({ ...source, currentPage: page.number })
                        }
                      >
                        page {page.number}
                      </button>
                    )}
                    {page.quote && (
                      <div
                        className="cs-media-button-menu-item-description"
                        dangerouslySetInnerHTML={{ __html: page.quote }}
                      />
                    )}
                  </div>
                ) : isInformational ? (
                  <span
                    className={`cs-media-button-menu-button cs-media-button-menu-button--static ${
                      index === 0
                        ? "cs-media-button-menu-button--no-margin"
                        : ""
                    }`}
                  >
                    Page {page.number}
                    {!!page.lastNumber &&
                      page.number !== page.lastNumber &&
                      `-${page.lastNumber}`}
                  </span>
                ) : (
                  <Button
                    className={`cs-media-button-menu-button ${
                      index === 0
                        ? "cs-media-button-menu-button--no-margin"
                        : ""
                    }`}
                    variant="text"
                    onClick={() =>
                      handleLink({ ...source, currentPage: page.number })
                    }
                  >
                    Page {page.number}
                    {!!page.lastNumber &&
                      page.number !== page.lastNumber &&
                      `-${page.lastNumber}`}
                  </Button>
                )}
              </Fragment>
            ))}
          {hasTimes(source) &&
            source.timeList!.map((time, index) => (
              <Fragment key={`time-${index}-${time.startSeconds}`}>
                {showQuotes ? (
                  <div className="cs-media-button-menu-item">
                    {index > 0 && (
                      <div className="cs-media-button-menu-item-separator" />
                    )}
                    {isInformational ? (
                      <span className="cs-media-button-menu-item-title cs-media-button-menu-item-title--static">
                        {timeLabel(time)}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="cs-media-button-menu-item-title"
                        onClick={() =>
                          handleLink({
                            ...source,
                            currentTime: time.startSeconds,
                          })
                        }
                      >
                        {timeLabel(time)}
                      </button>
                    )}
                    {time.quote && (
                      <div
                        className="cs-media-button-menu-item-description"
                        dangerouslySetInnerHTML={{ __html: time.quote }}
                      />
                    )}
                  </div>
                ) : isInformational ? (
                  <span
                    className={`cs-media-button-menu-button cs-media-button-menu-button--static ${
                      index === 0
                        ? "cs-media-button-menu-button--no-margin"
                        : ""
                    }`}
                  >
                    {timeLabel(time)}
                  </span>
                ) : (
                  <Button
                    className={`cs-media-button-menu-button ${
                      index === 0
                        ? "cs-media-button-menu-button--no-margin"
                        : ""
                    }`}
                    variant="text"
                    onClick={() =>
                      handleLink({
                        ...source,
                        currentTime: time.startSeconds,
                      })
                    }
                  >
                    {timeLabel(time)}
                  </Button>
                )}
              </Fragment>
            ))}
        </div>
      </Popover>
    </Fragment>
  );
}

/** Default inline avatar shown when theme.botAvatarUrl is not provided. */
function DefaultBotAvatar() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="16" cy="16" r="16" fill="currentColor" opacity="0.15" />
      <path
        d="M16 9a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm-7 14a7 7 0 0 1 14 0v1H9v-1z"
        fill="currentColor"
        opacity="0.7"
      />
    </svg>
  );
}

export function ChatBubble({
  id,
  message,
  isLoading,
  type,
  data,
  metadata,
  handleLink,
  handleSheetView,
  handleTivaDataResult,
  handleSummarize,
  handleReview,
  isActive = false,
  review,
  showReview = false,
  showSources = false,
  showSourcesQuotes = false,
  showSummary = false,
  hideAvatars = false,
  isExpanded = false,
  setExpanded,
  lang,
  activityFeed,
  bestEffort = false,
  toolErrors,
}: ChatBubbleProps) {
  const { currentUser, theme, icons } = useChatSurfaceContext();
  const assistantName = theme.assistantName ?? "Data results";
  const botAvatarUrl = theme.botAvatarUrl;
  const ThumbsUp = icons.ThumbsUp;
  const ThumbsDown = icons.ThumbsDown;
  const Diagram = icons.Diagram;
  const Summary = icons.Summary;
  const ArrowExpand = icons.ArrowExpand;
  const ArrowCollapse = icons.ArrowCollapse;

  const [messageElement, setMessageElement] = useState<HTMLElement | null>(
    null,
  );
  const isQuestion = type === ChatBubbleType.question;
  const isError = type === ChatBubbleType.error;

  // ---- Agentic citations (P2-09, design §9.2/§9.4). Agentic metadata items
  // carry a `marker` key; legacy metadata does not — that distinction picks
  // the renderer. Inline 【e1】 markers are placeholders: invisible until
  // their citation is known (streaming), then rendered as chips linking to
  // the source list entry.
  const agenticCitations: AgenticCitation[] =
    !isQuestion && Array.isArray(metadata) && metadata[0]?.marker != null
      ? (metadata as AgenticCitation[])
      : [];
  const isAgentic = agenticCitations.length > 0;
  const chipNumber = new Map<string, number>();
  agenticCitations.forEach((c, i) => chipNumber.set(c.marker, i + 1));

  const processedMessage = isQuestion
    ? message
    : message.replace(/【([^】]*)】/g, (_full, inner: string) => {
        const nums = inner
          .split(",")
          .map((s) => chipNumber.get(s.trim()))
          .filter((n): n is number => n != null);
        return nums.length
          ? nums.map((n) => `[${n}](#cs-cite-${id ?? "x"}-${n})`).join(" ")
          : "";
      });

  const locatorLabel = (c: AgenticCitation): string => {
    const loc = c.locator;
    if (!loc) return "";
    if (loc.kind === "sheet") {
      // Spreadsheet citations (sheet-citations design §3.5): value is the
      // ABSOLUTE sheet row of a verified quote, else null — then label the
      // chunk's absolute row range from meta. Chunks digested before the
      // row_numbers backfill have no absolute rows and degrade to the sheet
      // name alone (never a guessed row number). CSV's pseudo-sheet name
      // (derived from the file name) is noise, so csv labels rows only.
      const meta = (c.meta ?? {}) as Record<string, unknown>;
      const sheet =
        c.source_type === "csv"
          ? ""
          : typeof meta.sheet_name === "string"
            ? meta.sheet_name
            : "";
      const a = meta.sheet_row_start;
      const b = meta.sheet_row_end;
      const rows =
        loc.value != null
          ? `row ${loc.value}`
          : typeof a === "number"
            ? typeof b === "number" && b !== a
              ? `rows ${a}–${b}`
              : `row ${a}`
            : "";
      return [sheet, rows].filter(Boolean).join(" · ");
    }
    if (loc.value == null) return "";
    if (loc.kind === "page") return `p. ${loc.value}`;
    if (loc.kind === "timestamp") {
      // Media citations label like legacy timeLabel: "Chapter (17:58)" when
      // the transcript chunk has a chapter title, else just the time. The
      // ingestion-formatted start_time is preferred over re-deriving from
      // seconds so the label matches the source timeline exactly.
      const meta = (c.meta ?? {}) as Record<string, unknown>;
      const t =
        typeof meta.start_time === "string" && meta.start_time
          ? meta.start_time
          : fmtTime(Number(loc.value));
      const chapter =
        typeof meta.chapter_title === "string" ? meta.chapter_title : "";
      return chapter ? `${chapter} (${t})` : t;
    }
    return "";
  };

  // Legacy-parity citation links (P2-13). Agentic citations carry the same
  // link fields legacy metadata did (bucket/bucketFilePath/s3Service or
  // playUrl); entries without them (history-grounded answers, pre-P2-13
  // messages) fall back to static text. citationDisplay gates linking the
  // same way MediaButton does on the legacy path.
  const agenticMode = (c: AgenticCitation): CitationDisplayMode =>
    (c.citationDisplay as CitationDisplayMode) ?? "PAGES_LINKED";
  const agenticOpenable = (c: AgenticCitation): boolean =>
    c.locator?.kind === "sheet" && !!handleSheetView
      ? true
      : !!handleLink && (!!c.playUrl || (!!c.bucket && !!c.bucketFilePath));
  const agenticLinked = (c: AgenticCitation): boolean =>
    agenticMode(c) === "PAGES_LINKED" && agenticOpenable(c);
  const openAgentic = (c: AgenticCitation) => {
    const loc = c.locator;
    // Sheet locators open the in-app excerpt viewer when the host provides
    // one (sheet-citations design §4.3); otherwise fall through to the
    // whole-file open below — no URL fragment exists for a workbook.
    if (loc?.kind === "sheet" && handleSheetView) {
      handleSheetView({ ...c, messageId: id } as AgenticCitation);
      return;
    }
    const fragment =
      loc?.kind === "page" && loc.value != null
        ? { currentPage: Number(loc.value) }
        : loc?.kind === "timestamp" && loc.value != null
          ? { currentTime: Number(loc.value) }
          : {};
    handleLink?.({ ...c, ...fragment, messageId: id });
  };

  // Group by source (learning object) preserving rank order (§9.4).
  const citationGroups = (() => {
    const groups: {
      key: string;
      title: string;
      entries: { n: number; c: AgenticCitation }[];
    }[] = [];
    agenticCitations.forEach((c, i) => {
      const key = String(c.lo_item_id ?? c.sourceId ?? c.title ?? i);
      let g = groups.find((x) => x.key === key);
      if (!g) {
        g = { key, title: c.title || c.name || "Source", entries: [] };
        groups.push(g);
      }
      g.entries.push({ n: i + 1, c });
    });
    return groups;
  })();

  const contentClass = [
    "cs-chat-bubble-content",
    isQuestion
      ? "cs-chat-bubble-content--question"
      : "cs-chat-bubble-content--answer",
    isError ? "cs-chat-bubble-content--error" : "",
    isActive ? "cs-chat-bubble-content--active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Citation aggregation. Faithful to legacy behavior; types kept loose because
  // the metadata shape evolved across recognition pipelines.
  const sources: any[] = (() => {
    const filtered = (metadata ?? []).filter(
      (item: any) =>
        item.id_course !== undefined &&
        item.id_course !== null &&
        item.id_course !== "" &&
        item.lo_item_id !== undefined &&
        item.lo_item_id !== null &&
        item.lo_item_id !== "" &&
        // Backstop: HIDDEN resources are already dropped server-side.
        item.citationDisplay !== "HIDDEN",
    );
    const cleaned = filtered.map((item: any) => ({
      ...item,
      quote: (item.quote ?? "")
        .replace(/<img[^>]+src="(?!(http|data))[^"]+"[^>]+(\/>|><\/img>)/g, "")
        .replace(/<[^/>]+><\/[^>]+>/g, "")
        .replace(/<section[^>]+><\/section>/g, ""),
    }));

    const tagged = cleaned.reduce<any[]>((result, item) => {
      if (
        item.start_seconds != null &&
        Number.isFinite(item.start_seconds) &&
        isMedia(item.source_type)
      ) {
        return [
          ...result,
          {
            ...item,
            _isVideo: true,
            video_start_seconds: item.start_seconds,
            video_end_seconds: item.end_seconds,
            video_start_label: item.start_time || "",
            video_end_label: item.end_time || "",
            video_chapter: item.chapter_title || item.chapter || "",
          },
        ];
      }
      return [...result, item];
    }, []);

    const grouped = tagged.reduce<any[]>((result, item) => {
      const existing = result.find(
        (r) =>
          r.id_course === item.id_course && r.lo_item_id === item.lo_item_id,
      );
      if (item._isVideo) {
        const newEntry = {
          startSeconds: item.video_start_seconds,
          endSeconds: item.video_end_seconds,
          startLabel: item.video_start_label,
          endLabel: item.video_end_label,
          chapterTitle: item.video_chapter,
          relevance: item.relevance,
          score: item.score,
          quote: item.quote,
        };
        if (existing) {
          return result.map((r) =>
            r === existing
              ? { ...r, timeList: [...(r.timeList ?? []), newEntry] }
              : r,
          );
        }
        return [...result, { ...item, timeList: [newEntry] }];
      }
      // PDF / text branch — accumulate pageList
      if (existing) {
        return result.map((r) =>
          r === existing
            ? {
                ...r,
                pageList: [
                  ...(r.pageList ?? []),
                  {
                    number: item.page_number,
                    lastNumber: item.last_page_number,
                    relevance: item.relevance,
                    score: item.score,
                    quote: item.quote,
                  },
                ],
              }
            : r,
        );
      }
      return [
        ...result,
        {
          ...item,
          pageList: [
            {
              number: item.page_number,
              lastNumber: item.last_page_number,
              relevance: item.relevance,
              score: item.score,
              quote: item.quote,
            },
          ],
        },
      ];
    }, []);

    return grouped.map((item) => ({
      ...item,
      pageList: item.pageList
        ? [...item.pageList].sort(
            (a: any, b: any) => (b.relevance ?? 0) - (a.relevance ?? 0),
          )
        : item.pageList,
      timeList: item.timeList
        ? [...item.timeList].sort((a: any, b: any) => {
            const byRel = (b.relevance ?? 0) - (a.relevance ?? 0);
            return byRel !== 0
              ? byRel
              : (a.startSeconds ?? 0) - (b.startSeconds ?? 0);
          })
        : item.timeList,
    }));
  })();

  const messageRef = useCallback((node: HTMLElement | null) => {
    setMessageElement(node);
  }, []);

  const isClamped =
    type === ChatBubbleType.question &&
    !!id &&
    !!messageElement &&
    (messageElement.scrollHeight > messageElement.clientHeight ||
      messageElement.clientHeight > 80);

  const userInitial =
    currentUser?.firstName?.toUpperCase()?.charAt(0) ||
    currentUser?.email?.toUpperCase()?.charAt(0) ||
    "?";

  return (
    <div
      className="cs-chat-bubble"
      style={{ justifyContent: isQuestion ? "flex-end" : "flex-start" }}
    >
      {!hideAvatars && type !== ChatBubbleType.question && (
        <div className="cs-chat-bubble-avatar cs-chat-bubble-avatar--answer">
          {botAvatarUrl ? (
            <img src={botAvatarUrl} alt="" />
          ) : (
            <DefaultBotAvatar />
          )}
        </div>
      )}
      <div className={contentClass}>
        {data && type !== ChatBubbleType.error && (
          <span className="cs-chat-bubble-content__title">
            {assistantName}:
          </span>
        )}
        <span
          ref={messageRef}
          className={`cs-chat-bubble-content__text ${
            isExpanded ? "cs-chat-bubble-content__text--expanded" : ""
          }`}
          // Assistant bubbles (answer/summary/error/tivaData) are polite live
          // regions so streamed responses are announced (WCAG 4.1.3). Question
          // bubbles echo the user's own input and need no announcement. polite
          // coalesces rapid stream updates so the settled answer is read once.
          aria-live={isQuestion ? undefined : "polite"}
          aria-atomic={isQuestion ? undefined : "true"}
          // Mark a non-English answer's language so a screen reader switches
          // pronunciation (WCAG 3.1.2). Only on answers — question bubbles are
          // the user's own text, already in the page language.
          lang={!isQuestion ? lang : undefined}
        >
          <Markdown
            remarkPlugins={[remarkGfm]}
            components={{
              // Inline citation markers are NOT links: as anchors, VoiceOver
              // announced them as followable links (and listed them in the
              // links rotor) while the in-page jump did nothing useful. They
              // render as plain text with a visually-hidden "citation" prefix
              // so a screen reader announces "citation 1" inline and moves on.
              a: ({ href, children, ...props }) =>
                href?.startsWith("#cs-cite-") ? (
                  <span className="cs-citation-chip">
                    <span className="cs-sr-only">citation </span>
                    {children}
                  </span>
                ) : (
                  <a href={href} {...props}>
                    {children}
                  </a>
                ),
            }}
          >
            {processedMessage}
          </Markdown>
        </span>
        {!isQuestion && activityFeed && (
          <AgenticActivityFeed feed={activityFeed} live={isLoading} />
        )}
        {!isQuestion && isLoading && <DotsTyping />}
        {!!toolErrors?.length && !isLoading && (
          <div
            className="cs-agentic-banner cs-agentic-banner--error"
            role="note"
          >
            {toolErrors.length === 1
              ? "A technical error occurred while answering — the answer may be incomplete:"
              : "Technical errors occurred while answering — the answer may be incomplete:"}
            <ul className="cs-agentic-banner__errors">
              {toolErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}
        {bestEffort && !isLoading && (
          <div className="cs-agentic-banner" role="note">
            This is a partial answer — the assistant reached its work limit and
            answered with the information gathered so far.
          </div>
        )}
        {isAgentic && showSources && (
          <div className="cs-chat-bubble-metadata">
            <div className="cs-chat-bubble-metadata__title">Sources:</div>
            <ol className="cs-agentic-citations">
              {citationGroups.map((group) => (
                <li key={group.key} className="cs-agentic-citation">
                  <span className="cs-agentic-citation__title">
                    {group.title}
                  </span>
                  {group.entries.map(({ n, c }) => {
                    // Each chip is paired with ITS page — a group-level chip
                    // row + deduped page list left the reader unable to tell
                    // which citation was on which page. The title itself never
                    // links; only cited pages do.
                    const label =
                      agenticMode(c) === "FILE_ONLY" ? "" : locatorLabel(c);
                    return (
                      <span key={n} className="cs-agentic-citation__entry">
                        <span className="cs-agentic-citation__chip">
                          <span className="cs-sr-only">citation </span>
                          {n}
                        </span>
                        {!!label &&
                          (agenticLinked(c) ? (
                            <button
                              type="button"
                              className="cs-agentic-citation__locator cs-agentic-citation__link"
                              aria-label={`Open ${group.title}, ${label}`}
                              onClick={() => openAgentic(c)}
                            >
                              {label}
                            </button>
                          ) : (
                            <span className="cs-agentic-citation__locator">
                              {label}
                            </span>
                          ))}
                      </span>
                    );
                  })}
                </li>
              ))}
            </ol>
          </div>
        )}
        {!isAgentic && showSources && sources.length > 0 && (
          <div className="cs-chat-bubble-metadata">
            <div className="cs-chat-bubble-metadata__title">
              Relevant resources:
            </div>
            <div className="cs-chat-bubble-metadata__resources">
              {sources.map((item: any, idx: number) => (
                <div
                  key={`src-${idx}-${item.lo_item_id ?? ""}`}
                  className="cs-chat-bubble-resource"
                >
                  <MediaButton
                    className="cs-chat-bubble-resource--link"
                    source={item}
                    showQuotes={showSourcesQuotes}
                    handleLink={(arg) =>
                      handleLink?.({ ...arg, messageId: id })
                    }
                  />
                  {showSummary && handleSummarize && (
                    <Button
                      className="cs-chat-bubble-resource--summary"
                      variant="text"
                      startIcon={<Summary />}
                      onClick={() =>
                        handleSummarize({
                          courseId: item.id_course,
                          loItemId: item.lo_item_id,
                        })
                      }
                    >
                      AI summary
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {data?.payload && type !== ChatBubbleType.error && (
          <div className="cs-chat-bubble-data">
            <Button
              className="cs-chat-bubble-data__button"
              variant="text"
              startIcon={
                <Diagram className="cs-chat-bubble-data__button-icon" />
              }
              onClick={() => handleTivaDataResult?.(data)}
            >
              <span className="cs-chat-bubble-data__button-text">
                Open {assistantName} Results
              </span>
            </Button>
          </div>
        )}
        {showReview &&
          (type === ChatBubbleType.answer ||
            type === ChatBubbleType.tivaData) && (
            <div className="cs-chat-bubble-actions">
              <IconButton
                className={`cs-chat-bubble-actions__button-icon ${
                  review === Feedback.like
                    ? "cs-chat-bubble-actions__button-icon--fill"
                    : ""
                }`}
                aria-label="thumbs up"
                aria-pressed={review === Feedback.like}
                onClick={() =>
                  id != null &&
                  handleReview?.(
                    id,
                    review === Feedback.like
                      ? Feedback.noReview
                      : Feedback.like,
                  )
                }
              >
                <ThumbsUp />
              </IconButton>
              <IconButton
                className={`cs-chat-bubble-actions__button-icon ${
                  review === Feedback.dislike
                    ? "cs-chat-bubble-actions__button-icon--fill"
                    : ""
                }`}
                aria-label="thumbs down"
                aria-pressed={review === Feedback.dislike}
                onClick={() =>
                  id != null &&
                  handleReview?.(
                    id,
                    review === Feedback.dislike
                      ? Feedback.noReview
                      : Feedback.dislike,
                  )
                }
              >
                <ThumbsDown />
              </IconButton>
            </div>
          )}
        {isClamped && (
          <div
            className={`cs-chat-bubble-collapse ${
              isExpanded ? "cs-chat-bubble-collapse--hidden" : ""
            }`}
          >
            <div className="cs-chat-bubble-collapse__gradient" />
            <div className="cs-chat-bubble-collapse__button">
              <IconButton
                aria-label={isExpanded ? "collapse" : "expand"}
                onClick={() => setExpanded?.(!isExpanded)}
              >
                {isExpanded ? <ArrowCollapse /> : <ArrowExpand />}
              </IconButton>
            </div>
          </div>
        )}
      </div>
      {!hideAvatars && currentUser && type === ChatBubbleType.question && (
        <div className="cs-chat-bubble-avatar cs-chat-bubble-avatar--question">
          {currentUser.avatarUrl ? (
            <img src={currentUser.avatarUrl} alt="" />
          ) : (
            <div className="cs-chat-bubble-avatar-initial">{userInitial}</div>
          )}
        </div>
      )}
    </div>
  );
}
